import { useEffect, useRef, useState } from "react";
import { transcribeAudio } from "./speech";

export function recordingMime(
  supported: (mime: string) => boolean,
): string | undefined {
  return [
    "audio/webm;codecs=opus",
    "audio/mp4",
    "audio/webm",
    "audio/ogg;codecs=opus",
  ].find(supported);
}

export function useVoiceRecorder<Context>(
  onTranscript: (
    text: string,
    signal: AbortSignal,
    context: Context,
  ) => Promise<void>,
) {
  const [state, setState] = useState<
    "idle" | "recording" | "transcribing" | "acting"
  >("idle");
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");
  const current = useRef(onTranscript);
  current.current = onTranscript;
  const recorder = useRef<MediaRecorder | null>(null),
    stream = useRef<MediaStream | null>(null);
  const audio = useRef<AudioContext | null>(null),
    cancelJob = useRef<AbortController | null>(null);
  const epoch = useRef(0),
    busy = useRef(false);
  const interval = useRef<ReturnType<typeof setInterval> | null>(null),
    limit = useRef<ReturnType<typeof setTimeout> | null>(null);
  function release() {
    if (interval.current) clearInterval(interval.current);
    if (limit.current) clearTimeout(limit.current);
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    void audio.current?.close().catch(() => {});
    audio.current = null;
  }
  function cancel() {
    epoch.current++;
    cancelJob.current?.abort();
    try {
      if (recorder.current?.state === "recording") recorder.current.stop();
    } catch {}
    release();
    busy.current = false;
    setState("idle");
  }
  function stop() {
    if (recorder.current?.state === "recording") recorder.current.stop();
    release();
  }
  useEffect(() => {
    const hide = () => {
      if (document.hidden) cancel();
    };
    const stopped = () => cancel();
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("derech:media-stopped", stopped);
    return () => {
      epoch.current++;
      cancelJob.current?.abort();
      release();
      try {
        if (recorder.current?.state === "recording") recorder.current.stop();
      } catch {}
      document.removeEventListener("visibilitychange", hide);
      window.removeEventListener("derech:media-stopped", stopped);
    };
  }, []);
  async function start(context: Context) {
    if (busy.current) return;
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setError("הקלטה אינה נתמכת בדפדפן הזה. השתמשו באפליקציה המעודכנת.");
      return;
    }
    busy.current = true;
    const run = ++epoch.current;
    setError("");
    setTranscript("");
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: false,
      });
      if (run !== epoch.current) {
        media.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.current = media;
      const mime = recordingMime((m) => MediaRecorder.isTypeSupported(m));
      const capture = new MediaRecorder(
        media,
        mime ? { mimeType: mime } : undefined,
      );
      recorder.current = capture;
      const chunks: Blob[] = [];
      let size = 0;
      capture.ondataavailable = (event) => {
        if (run !== epoch.current) return;
        if (event.data.size) {
          chunks.push(event.data);
          size += event.data.size;
          if (size > 2_000_000) stop();
        }
      };
      capture.onerror = () => {
        if (run === epoch.current) {
          cancel();
          setError("ההקלטה נכשלה. בדקו את הרשאת המיקרופון.");
        }
      };
      capture.onstop = async () => {
        if (run !== epoch.current) return;
        release();
        try {
          const blob = new Blob(chunks, {
            type: capture.mimeType || mime || "audio/webm",
          });
          if (blob.size < 100 || blob.size > 2_097_152)
            throw new Error("ההקלטה קצרה מדי או גדולה מדי. נסו פקודה קצרה.");
          setState("transcribing");
          const controller = new AbortController();
          cancelJob.current = controller;
          const timeout = setTimeout(() => controller.abort(), 90_000);
          let text: string;
          try {
            text = await transcribeAudio(blob, controller.signal);
          } finally {
            clearTimeout(timeout);
          }
          if (run !== epoch.current) return;
          setTranscript(text);
          setState("acting");
          // Keep the question from this recording, even if the current UI and
          // callback change while microphone access or transcription is pending.
          await current.current(text, controller.signal, context);
        } catch (e) {
          if (run === epoch.current)
            setError(
              (e as Error).name === "AbortError"
                ? "זיהוי הדיבור ארך יותר מדי זמן. נסו שוב."
                : (e as Error).message,
            );
        } finally {
          if (run === epoch.current) {
            busy.current = false;
            setState("idle");
          }
        }
      };
      capture.start(200);
      setState("recording");
      limit.current = setTimeout(stop, 10_000);
      try {
        const context = new AudioContext();
        audio.current = context;
        await context.resume();
        if (run !== epoch.current || capture.state !== "recording") return;
        const analyser = context.createAnalyser();
        analyser.fftSize = 1024;
        context.createMediaStreamSource(media).connect(analyser);
        const samples = new Uint8Array(analyser.fftSize);
        let heard = false,
          lastSound = Date.now();
        const began = Date.now();
        interval.current = setInterval(() => {
          analyser.getByteTimeDomainData(samples);
          let sum = 0;
          for (const value of samples) sum += ((value - 128) / 128) ** 2;
          if (Math.sqrt(sum / samples.length) > 0.018) {
            heard = true;
            lastSound = Date.now();
          }
          if (
            heard &&
            Date.now() - lastSound > 1100 &&
            Date.now() - began > 1400
          )
            stop();
        }, 100);
      } catch {
        /* Manual stop and the bounded ten-second timer remain available. */
      }
    } catch (e) {
      if (run === epoch.current) {
        release();
        busy.current = false;
        setState("idle");
        setError(
          (e as Error).name === "NotAllowedError"
            ? "נדרשת הרשאת מיקרופון לדיווח קולי."
            : "לא ניתן לפתוח את המיקרופון כרגע.",
        );
      }
    }
  }
  return {
    state,
    transcript,
    error,
    start,
    stop,
    cancel,
    clear: () => {
      setTranscript("");
      setError("");
    },
  };
}
